import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import { 
  FileText, 
  Save,
  ArrowLeft,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { invoicesAPI, clientsAPI, servicesAPI, profileAPI } from '../services/api';

interface InvoiceItem {
  serialNumber: number;
  description: string;
  sac: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface Client {
  _id: string;
  clientName: string;
  email: string;
  contactNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  gstin?: string;
  taxType?: 'IGST' | 'SGST_CGST';
  placeOfSupply: string;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  client: Client | string;
  items: InvoiceItem[];
  subtotal: number;
  taxableAmount: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalAmount: number;
  totalAmountInWords: string;
  status: string;
  notes?: string;
  paymentTerms: string;
}

interface Service {
  _id: string;
  description: string;
  sac: string;
  price: number;
}

const EditableInvoiceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [gstRate, setGstRate] = useState<number>(18);
  const [showSuggestions, setShowSuggestions] = useState<{ [key: number]: boolean }>({});
  const [filteredSuggestions, setFilteredSuggestions] = useState<{ [key: number]: Service[] }>({});

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      invoiceNumber: '',
      invoiceDate: '',
      dueDate: '',
      client: '',
      items: [{
        description: '',
        sac: '',
        quantity: 1,
        rate: 0
      }],
      notes: '',
      paymentTerms: '',
      status: 'Draft'
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedItems = watch('items');
  const watchedClient = watch('client');
  const watchedDueDate = watch('dueDate');

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    
    try {
      const [invoiceRes, clientsRes, servicesRes, profileRes] = await Promise.all([
        invoicesAPI.getById(id),
        clientsAPI.getAll({ limit: 1000 }),
        servicesAPI.getAll({ isActive: 'true' }),
        profileAPI.get().catch(() => null)
      ]);
      
      const invoiceData = invoiceRes.data;
      setInvoice(invoiceData);
      setClients(clientsRes.data.clients);
      setServices(servicesRes.data);
      
      if (profileRes?.data?.defaultGstRate) {
        setGstRate(profileRes.data.defaultGstRate);
      }

      // Set selected client
      const client = typeof invoiceData.client === 'object' ? invoiceData.client : 
        clientsRes.data.clients.find((c: Client) => c._id === invoiceData.client);
      setSelectedClient(client || null);

      // Populate form with invoice data
      reset({
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: invoiceData.invoiceDate.split('T')[0],
        dueDate: invoiceData.dueDate.split('T')[0],
        client: typeof invoiceData.client === 'object' ? invoiceData.client._id : invoiceData.client,
        items: invoiceData.items.map((item: InvoiceItem) => ({
          description: item.description,
          sac: item.sac || '',
          quantity: item.quantity,
          rate: item.rate
        })),
        notes: invoiceData.notes || '',
        paymentTerms: invoiceData.paymentTerms || '',
        status: invoiceData.status
      });
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setMessage({ type: 'error', text: 'Failed to load invoice' });
    } finally {
      setLoading(false);
    }
  }, [id, reset]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.autocomplete-wrapper')) {
        setShowSuggestions({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (watchedClient) {
      const client = clients.find(c => c._id === watchedClient);
      setSelectedClient(client || null);
      if (client?.taxType) {
        // Recalculate taxes when client changes
        const totals = calculateTotals();
        // Update form values if needed
      }
    }
  }, [watchedClient, clients]);

  const handleClientSelect = (clientId: string) => {
    const client = clients.find(c => c._id === clientId);
    setSelectedClient(client || null);
    setValue('client', clientId);
  };

  const addItem = () => {
    append({
      description: '',
      sac: '',
      quantity: 1,
      rate: 0
    });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const calculateAmount = (quantity: number, rate: number) => {
    return quantity * rate;
  };

  const calculateTotals = () => {
    const items = watchedItems || [];
    const subtotal = items.reduce((sum, item) => sum + calculateAmount(item.quantity || 0, item.rate || 0), 0);
    const taxableAmount = subtotal;
    
    const taxType = selectedClient?.taxType || 'SGST_CGST';
    
    let cgstRate = 0;
    let sgstRate = 0;
    let igstRate = 0;
    
    if (taxType === 'IGST') {
      igstRate = gstRate;
      cgstRate = 0;
      sgstRate = 0;
    } else {
      cgstRate = gstRate / 2;
      sgstRate = gstRate / 2;
      igstRate = 0;
    }
    
    const cgstAmount = (taxableAmount * cgstRate) / 100;
    const sgstAmount = (taxableAmount * sgstRate) / 100;
    const igstAmount = (taxableAmount * igstRate) / 100;
    
    // Round off final total amount
    const totalAmount = Math.round(taxableAmount + cgstAmount + sgstAmount + igstAmount);
    
    return {
      subtotal,
      taxableAmount,
      cgstRate,
      sgstRate,
      igstRate,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalAmount,
      taxType
    };
  };

  const totals = calculateTotals();

  const handleDescriptionChange = (index: number, value: string) => {
    setValue(`items.${index}.description`, value as any);
    
    if (value.trim() === '') {
      setShowSuggestions({ ...showSuggestions, [index]: false });
      return;
    }

    const filtered = services.filter(service =>
      service.description.toLowerCase().includes(value.toLowerCase())
    );
    
    setFilteredSuggestions({ ...filteredSuggestions, [index]: filtered });
    setShowSuggestions({ ...showSuggestions, [index]: filtered.length > 0 });
  };

  const handleServiceSelect = (index: number, service: Service) => {
    setValue(`items.${index}.description`, service.description);
    setValue(`items.${index}.sac`, service.sac);
    setValue(`items.${index}.rate`, service.price);
    setShowSuggestions({ ...showSuggestions, [index]: false });
  };

  const onSubmit = async (data: any) => {
    if (!invoice) return;
    
    setSaving(true);
    setMessage(null);

    try {
      const itemsWithAmounts = data.items.map((item: any, index: number) => ({
        ...item,
        serialNumber: index + 1,
        amount: calculateAmount(item.quantity || 0, item.rate || 0)
      }));

      const updateData = {
        ...data,
        items: itemsWithAmounts,
        invoiceDate: data.invoiceDate,
        dueDate: data.dueDate,
        ...totals,
        totalAmountInWords: numberToWords(Math.round(totals.totalAmount))
      };

      await invoicesAPI.update(invoice._id, updateData);
      setMessage({ type: 'success', text: 'Invoice updated successfully!' });
      
      // Refresh invoice data
      setTimeout(() => {
        fetchInvoice();
      }, 1000);
    } catch (error) {
      console.error('Error updating invoice:', error);
      setMessage({ type: 'error', text: 'Failed to update invoice' });
    } finally {
      setSaving(false);
    }
  };

  const numberToWords = (num: number) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    if (num === 0) return 'Zero';
    
    function convertHundreds(n: number): string {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result.trim();
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result.trim();
    }
    
    let result = '';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = num % 1000;
    
    if (crore > 0) {
      result += convertHundreds(crore) + ' Crore ';
    }
    if (lakh > 0) {
      result += convertHundreds(lakh) + ' Lakh ';
    }
    if (thousand > 0) {
      result += convertHundreds(thousand) + ' Thousand ';
    }
    if (hundred > 0) {
      result += convertHundreds(hundred) + ' ';
    }
    
    return result.trim() + ' Rupees Only';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-8">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/revised')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-3">
            <FileText className="h-8 w-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit Invoice: {invoice.invoiceNumber}</h1>
              <p className="text-gray-600">Revise invoice details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Invoice Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Information</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <label className="form-label">Invoice Number *</label>
              <input
                type="text"
                {...register('invoiceNumber', { required: 'Invoice number is required' })}
                className="form-input"
              />
              {errors.invoiceNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.invoiceNumber.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Invoice Date *</label>
              <input
                type="date"
                {...register('invoiceDate', { required: 'Invoice date is required' })}
                className="form-input"
              />
              {errors.invoiceDate && (
                <p className="mt-1 text-sm text-red-600">{errors.invoiceDate.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Due Date *</label>
              <input
                type="date"
                {...register('dueDate', { required: 'Due date is required' })}
                className="form-input"
              />
              {errors.dueDate && (
                <p className="mt-1 text-sm text-red-600">{errors.dueDate.message}</p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label className="form-label">Select Client *</label>
            <select
              {...register('client', { required: 'Client is required' })}
              onChange={(e) => handleClientSelect(e.target.value)}
              className="form-input"
            >
              <option value="">Choose a client...</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.clientName} - {client.email}
                </option>
              ))}
            </select>
            {errors.client && (
              <p className="mt-1 text-sm text-red-600">{errors.client.message}</p>
            )}
          </div>

          {selectedClient && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 p-4 bg-gray-50 rounded-lg"
            >
              <div className="text-sm text-gray-600 space-y-1">
                <p className="font-medium text-gray-900">{selectedClient.clientName}</p>
                <p>{selectedClient.email} • {selectedClient.contactNumber}</p>
                <p>{selectedClient.address.street}, {selectedClient.address.city}, {selectedClient.address.state} - {selectedClient.address.pincode}</p>
                {selectedClient.gstin && <p>GSTIN: {selectedClient.gstin}</p>}
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Invoice Items */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
          style={{ overflow: 'visible' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Invoice Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Item</span>
            </button>
          </div>

          <div className="overflow-x-auto" style={{ position: 'relative' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header w-16">S.No.</th>
                  <th className="table-header">Description</th>
                  <th className="table-header w-40">SAC</th>
                  <th className="table-header w-28">Quantity</th>
                  <th className="table-header w-36">Rate</th>
                  <th className="table-header w-36">Amount</th>
                  <th className="table-header w-16">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields.map((field, index) => (
                  <tr key={field.id}>
                    <td className="table-cell text-center">
                      {index + 1}
                    </td>
                    <td className="table-cell" style={{ position: 'relative' }}>
                      <div className="relative autocomplete-wrapper">
                        <input
                          type="text"
                          {...register(`items.${index}.description`)}
                          onChange={(e) => handleDescriptionChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter or select service"
                          autoComplete="off"
                        />
                        {showSuggestions[index] && filteredSuggestions[index] && filteredSuggestions[index].length > 0 && (
                          <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-2xl max-h-48 overflow-auto" style={{ 
                            position: 'absolute', 
                            left: 0, 
                            bottom: '100%',
                            marginBottom: '4px'
                          }}>
                            {filteredSuggestions[index].map((service) => (
                              <div
                                key={service._id}
                                onClick={() => handleServiceSelect(index, service)}
                                className="px-4 py-2 hover:bg-primary-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900">{service.description}</div>
                                <div className="text-sm text-gray-500">
                                  SAC: {service.sac} | Price: ₹{service.price.toFixed(2)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <input
                        type="text"
                        {...register(`items.${index}.sac`)}
                        className="form-input w-full"
                        placeholder="SAC"
                      />
                    </td>
                    <td className="table-cell">
                      <input
                        type="number"
                        {...register(`items.${index}.quantity`, { valueAsNumber: true, min: 1 })}
                        className="form-input w-full"
                        placeholder="Qty"
                        min="1"
                        step="1"
                      />
                    </td>
                    <td className="table-cell">
                      <input
                        type="number"
                        {...register(`items.${index}.rate`, { valueAsNumber: true, min: 0 })}
                        className="form-input w-full"
                        placeholder="Rate"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="table-cell font-medium">
                      {formatCurrency(calculateAmount(watchedItems?.[index]?.quantity || 0, watchedItems?.[index]?.rate || 0))}
                    </td>
                    <td className="table-cell">
                      {fields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Totals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Totals</h2>
          <div className="flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Taxable Amount:</span>
                <span className="font-medium">{formatCurrency(totals.taxableAmount)}</span>
              </div>
              {totals.cgstRate > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">CGST ({totals.cgstRate}%):</span>
                  <span className="font-medium">{formatCurrency(totals.cgstAmount)}</span>
                </div>
              )}
              {totals.sgstRate > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">SGST ({totals.sgstRate}%):</span>
                  <span className="font-medium">{formatCurrency(totals.sgstAmount)}</span>
                </div>
              )}
              {totals.igstRate > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">IGST ({totals.igstRate}%):</span>
                  <span className="font-medium">{formatCurrency(totals.igstAmount)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                  <span className="text-lg font-bold text-primary-600">{formatCurrency(totals.totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Notes and Payment Terms */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Notes</label>
              <textarea
                {...register('notes')}
                className="form-input"
                rows={4}
                placeholder="Additional notes..."
              />
            </div>
            <div>
              <label className="form-label">Payment Terms *</label>
              <textarea
                {...register('paymentTerms', { required: 'Payment terms are required' })}
                className="form-input"
                rows={4}
                placeholder="Payment terms..."
              />
              {errors.paymentTerms && (
                <p className="mt-1 text-sm text-red-600">{errors.paymentTerms.message}</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Form Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-end space-x-4"
        >
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/revised')}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </motion.div>
      </form>
    </div>
  );
};

export default EditableInvoiceDetails;

